import { albumData } from '../data/albumData';

// ── Helpers ────────────────────────────────────────────────────────────────

function collectedCount(collected) {
  return Object.values(collected || {}).filter(Boolean).length;
}

function totalStickers() {
  let n = 0;
  albumData.sections.forEach(s => (n += s.stickers.length));
  albumData.groups.forEach(g => g.teams.forEach(t => (n += t.stickers.length)));
  return n;
}

function groupComplete(group, collected) {
  return group.teams.every(t => t.stickers.every(s => !!collected[s.number]));
}

function sectionComplete(section, collected) {
  return section.stickers.every(s => !!collected[s.number]);
}

function legendaryCount(collected, rarityMap) {
  return Object.keys(collected || {}).filter(k => collected[k] && rarityMap?.[k] < 5).length;
}

function completedTeams(collected) {
  let count = 0;
  albumData.groups.forEach(g =>
    g.teams.forEach(t => {
      if (t.stickers.every(s => !!collected[s.number])) count++;
    })
  );
  return count;
}

// ── Achievement definitions ────────────────────────────────────────────────
// Each entry: { id, icon, title, desc, category, check(state, extra) → { done, progress } }

export const ACHIEVEMENTS = [
  // ── Colección ──
  {
    id: 'first_sticker',
    icon: '✨',
    title: 'Primera Lámina',
    desc: 'Registra tu primera lámina en el álbum',
    category: 'coleccion',
    check: ({ collected }) => {
      const have = collectedCount(collected);
      return { done: have >= 1, progress: Math.min(have, 1), total: 1 };
    },
  },
  {
    id: 'fifty_stickers',
    icon: '📖',
    title: 'Empezando Fuerte',
    desc: 'Consigue 50 láminas',
    category: 'coleccion',
    check: ({ collected }) => {
      const have = collectedCount(collected);
      return { done: have >= 50, progress: Math.min(have, 50), total: 50 };
    },
  },
  {
    id: 'two_hundred',
    icon: '🎯',
    title: 'Coleccionista Serio',
    desc: 'Consigue 200 láminas',
    category: 'coleccion',
    check: ({ collected }) => {
      const have = collectedCount(collected);
      return { done: have >= 200, progress: Math.min(have, 200), total: 200 };
    },
  },
  {
    id: 'halfway',
    icon: '⚡',
    title: 'A Medio Camino',
    desc: 'Completa el 50% del álbum',
    category: 'coleccion',
    check: ({ collected }) => {
      const have = collectedCount(collected);
      const total = totalStickers();
      const pct = Math.round((have / total) * 100);
      return { done: pct >= 50, progress: pct, total: 50 };
    },
  },
  {
    id: 'ninety_pct',
    icon: '🔥',
    title: 'Casi Completo',
    desc: 'Alcanza el 90% del álbum',
    category: 'coleccion',
    check: ({ collected }) => {
      const have = collectedCount(collected);
      const total = totalStickers();
      const pct = Math.round((have / total) * 100);
      return { done: pct >= 90, progress: pct, total: 90 };
    },
  },
  {
    id: 'full_album',
    icon: '👑',
    title: 'Álbum Completo',
    desc: '¡Consigue todas las láminas del álbum!',
    category: 'coleccion',
    check: ({ collected }) => {
      const have = collectedCount(collected);
      const total = totalStickers();
      return { done: have >= total, progress: have, total };
    },
  },

  // ── Equipos y grupos ──
  {
    id: 'first_team',
    icon: '⚽',
    title: 'Equipo Completo',
    desc: 'Completa todas las láminas de un equipo',
    category: 'equipos',
    check: ({ collected }) => {
      const done = completedTeams(collected);
      return { done: done >= 1, progress: Math.min(done, 1), total: 1 };
    },
  },
  {
    id: 'five_teams',
    icon: '🏟️',
    title: 'Multi-Equipo',
    desc: 'Completa 5 equipos distintos',
    category: 'equipos',
    check: ({ collected }) => {
      const done = completedTeams(collected);
      return { done: done >= 5, progress: Math.min(done, 5), total: 5 };
    },
  },
  {
    id: 'first_group',
    icon: '🗂️',
    title: 'Grupo Completo',
    desc: 'Completa todos los equipos de un grupo',
    category: 'equipos',
    check: ({ collected }) => {
      const done = albumData.groups.filter(g => groupComplete(g, collected)).length;
      return { done: done >= 1, progress: Math.min(done, 1), total: 1 };
    },
  },
  {
    id: 'all_groups',
    icon: '🌍',
    title: 'Todos los Grupos',
    desc: 'Completa los 12 grupos del torneo',
    category: 'equipos',
    check: ({ collected }) => {
      const done = albumData.groups.filter(g => groupComplete(g, collected)).length;
      return { done: done >= albumData.groups.length, progress: done, total: albumData.groups.length };
    },
  },
  {
    id: 'museum',
    icon: '🏛️',
    title: 'Historiador',
    desc: 'Completa la sección Museo FIFA',
    category: 'equipos',
    check: ({ collected }) => {
      const section = albumData.sections.find(s => s.id === 'museum');
      const done = section ? sectionComplete(section, collected) : false;
      const have = section ? section.stickers.filter(s => !!collected[s.number]).length : 0;
      return { done, progress: have, total: section?.stickers.length ?? 0 };
    },
  },
  {
    id: 'intro',
    icon: '🎖️',
    title: 'Colección FWC',
    desc: 'Completa la sección de Introducción',
    category: 'equipos',
    check: ({ collected }) => {
      const section = albumData.sections.find(s => s.id === 'intro');
      const done = section ? sectionComplete(section, collected) : false;
      const have = section ? section.stickers.filter(s => !!collected[s.number]).length : 0;
      return { done, progress: have, total: section?.stickers.length ?? 0 };
    },
  },

  // ── Rareza ──
  {
    id: 'first_legendary',
    icon: '💎',
    title: 'Joya Encontrada',
    desc: 'Consigue tu primera lámina legendaria (<5% de coleccionistas)',
    category: 'rareza',
    check: ({ collected }, { rarityMap }) => {
      const count = legendaryCount(collected, rarityMap);
      return { done: count >= 1, progress: Math.min(count, 1), total: 1 };
    },
  },
  {
    id: 'five_legendaries',
    icon: '💠',
    title: 'Cazador de Joyas',
    desc: 'Consigue 5 láminas legendarias',
    category: 'rareza',
    check: ({ collected }, { rarityMap }) => {
      const count = legendaryCount(collected, rarityMap);
      return { done: count >= 5, progress: Math.min(count, 5), total: 5 };
    },
  },

  // ── Duplicados ──
  {
    id: 'ten_dupes',
    icon: '📦',
    title: 'Tengo Para Dar',
    desc: 'Acumula 10 láminas repetidas',
    category: 'trading',
    check: ({ duplicates }) => {
      const total = Object.values(duplicates || {}).reduce((s, v) => s + (v || 0), 0);
      return { done: total >= 10, progress: Math.min(total, 10), total: 10 };
    },
  },
  {
    id: 'fifty_dupes',
    icon: '🗃️',
    title: 'Almacén de Láminas',
    desc: 'Acumula 50 láminas repetidas',
    category: 'trading',
    check: ({ duplicates }) => {
      const total = Object.values(duplicates || {}).reduce((s, v) => s + (v || 0), 0);
      return { done: total >= 50, progress: Math.min(total, 50), total: 50 };
    },
  },

  // ── Trading ──
  {
    id: 'first_trade',
    icon: '🤝',
    title: 'Primer Intercambio',
    desc: 'Completa tu primer intercambio con otro coleccionista',
    category: 'trading',
    check: ({ history }) => {
      const count = (history || []).filter(h => h.type === 'trade').length;
      return { done: count >= 1, progress: Math.min(count, 1), total: 1 };
    },
  },
  {
    id: 'five_trades',
    icon: '🔄',
    title: 'Coleccionista Social',
    desc: 'Completa 5 intercambios',
    category: 'trading',
    check: ({ history }) => {
      const count = (history || []).filter(h => h.type === 'trade').length;
      return { done: count >= 5, progress: Math.min(count, 5), total: 5 };
    },
  },
  {
    id: 'ten_trades',
    icon: '🌟',
    title: 'Rey del Mercado',
    desc: 'Completa 10 intercambios',
    category: 'trading',
    check: ({ history }) => {
      const count = (history || []).filter(h => h.type === 'trade').length;
      return { done: count >= 10, progress: Math.min(count, 10), total: 10 };
    },
  },

  // ── Amigos ──
  {
    id: 'first_friend',
    icon: '👥',
    title: 'Primer Amigo',
    desc: 'Agrega a tu primer amigo de intercambio',
    category: 'social',
    check: ({ friends }) => {
      const count = Object.keys(friends || {}).length;
      return { done: count >= 1, progress: Math.min(count, 1), total: 1 };
    },
  },
  {
    id: 'five_friends',
    icon: '🫂',
    title: 'Red de Coleccionistas',
    desc: 'Agrega 5 amigos de intercambio',
    category: 'social',
    check: ({ friends }) => {
      const count = Object.keys(friends || {}).length;
      return { done: count >= 5, progress: Math.min(count, 5), total: 5 };
    },
  },
];

// ── Public API ─────────────────────────────────────────────────────────────

export const CATEGORIES = {
  coleccion: { label: 'Colección',  icon: '📖' },
  equipos:   { label: 'Equipos',    icon: '⚽' },
  rareza:    { label: 'Rareza',     icon: '💎' },
  trading:   { label: 'Intercambio',icon: '🤝' },
  social:    { label: 'Social',     icon: '👥' },
};

/** Returns array of { achievement, done, progress, total } for all achievements */
export function computeAchievements(state, extra = {}) {
  return ACHIEVEMENTS.map(a => {
    const result = a.check(state, extra);
    return { achievement: a, ...result };
  });
}

/**
 * Returns IDs of newly unlocked achievements comparing prev and current state.
 *
 * `prevExtra` defaults to `currExtra` for back-compat. Passing them
 * separately matters when an async-loaded dependency (rarityMap) flips
 * a check's verdict mid-session: without this, achievements that became
 * `done=true` only after rarityMap finished loading would never fire
 * the unlock notification because BOTH prev and curr would already see
 * them as `done`.
 */
export function detectNewlyUnlocked(prevState, currState, currExtra = {}, prevExtra = currExtra) {
  if (!prevState) return [];
  const prev = computeAchievements(prevState, prevExtra);
  const curr = computeAchievements(currState, currExtra);
  return curr
    .filter((c, i) => c.done && !prev[i].done)
    .map(c => c.achievement.id);
}
